import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import ProductCard from "@/components/ProductCard";
import { Heart, Gift, Truck, Home as HomeIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setFeaturedProducts(response.data.slice(0, 3));
      } catch (e) {
        console.error("Error fetching products:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const steps = [
    {
      icon: <Gift size={32} className="text-brand-primary" />,
      title: "Choose a Package",
      description: "Browse our curated selection of care packages designed for college students."
    },
    {
      icon: <Heart size={32} className="text-brand-coral" />,
      title: "Add a Personal Touch",
      description: "Include a heartfelt message to let your student know you're thinking of them."
    },
    {
      icon: <Truck size={32} className="text-brand-primary" />,
      title: "We Deliver with Care",
      description: "Your package is delivered directly to your student's dorm or campus address."
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-stretch">
            {/* Image on Left - Aligned with text height */}
            <div className="order-1 md:order-1 animate-fade-in-up flex">
              <div className="relative w-full flex flex-col">
                <img 
                  src="/hero-student.png"
                  alt="Happy college student on campus holding a cellophane-wrapped gift basket with a bow"
                  className="rounded-2xl shadow-lg w-full h-full object-cover"
                />
              </div>
            </div>
            {/* Text Content on Right - Aligned to top */}
            <div className="order-2 md:order-2 animate-fade-in-up animate-delay-200 flex flex-col justify-between">
              <div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-brand-dark leading-tight mb-6">
                  Send a Little <span className="text-[#9FBFD6]">Home</span> to Your Student
                </h1>
                <p className="font-body text-base md:text-lg text-brand-muted leading-relaxed mb-8 max-w-lg">
                  Curated care packages delivered to college students at the University of Tulsa and Oral Roberts University. Because sometimes, love is best delivered in a box.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/shop" 
                  className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                  data-testid="hero-shop-link"
                >
                  Browse Packages
                </Link>
                <Link 
                  to="/about" 
                  className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white border-2 border-brand-primary text-brand-primary font-body font-medium hover:bg-brand-background transition-all"
                  data-testid="hero-about-link"
                >
                  Our Story
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-brand-dark mb-4">
              How It Works
            </h2>
            <p className="font-body text-brand-muted max-w-2xl mx-auto">
              Sending comfort to your college student is simple and heartfelt.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="text-center p-8 rounded-xl bg-brand-background"
                data-testid={`how-it-works-step-${index + 1}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                  {step.icon}
                </div>
                <h3 className="font-heading text-xl text-brand-dark mb-3">{step.title}</h3>
                <p className="font-body text-sm text-brand-muted leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-28" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl text-brand-dark mb-4">
              Featured Care Packages
            </h2>
            <p className="font-body text-brand-muted max-w-2xl mx-auto">
              Each package is thoughtfully curated to bring comfort and joy to your student.
            </p>
          </div>
          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl h-80 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} showAddToCart={true} />
              ))}
            </div>
          )}
          <div className="text-center mt-12">
            <Link 
              to="/shop" 
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-brand-primary text-white font-body font-medium shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
              data-testid="view-all-products-link"
            >
              View All Packages
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 md:py-28 bg-brand-secondary/20" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-brand-dark mb-6">
            Trusted by Tulsa Families
          </h2>
          <p className="font-body text-brand-muted max-w-2xl mx-auto mb-12 text-lg leading-relaxed">
            We understand the unique bond between parents and their college students. Our care packages are designed to bridge the distance and deliver a warm reminder of home.
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="font-heading text-3xl text-brand-primary mb-2">4</p>
              <p className="font-body text-sm text-brand-muted">Universities Served</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="font-heading text-3xl text-brand-coral mb-2">Local</p>
              <p className="font-body text-sm text-brand-muted">Tulsa Based</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <p className="font-heading text-3xl text-brand-primary mb-2">Care</p>
              <p className="font-body text-sm text-brand-muted">In Every Package</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
